import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X, MoreHorizontal, Trash2, FileText, Crown, CheckSquare, Square, Copy, Download, ListTodo, FolderKanban, FolderPlus, GanttChart as GanttChartIcon, Edit, CheckCircle2, History, MessageCircle, Paperclip, Link2, Activity, Share2, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { formatDistanceToNow } from "date-fns";
import { API, PRIORITY_CONFIG } from "@/utils/constants";

export const TemplatesView = ({ templates, tags, onCreateTemplate, onDeleteTemplate, onUseTemplate, columns, isPro, onShowUpgrade }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: '', title: '', description: '', priority: 'medium', subtasks: [], tag_ids: [] });
  const [newSubtask, setNewSubtask] = useState('');

  if (!isPro) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto">
          <Copy className="h-16 w-16 text-primary mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold mb-2">Unlock Task Templates</h3>
          <p className="text-muted-foreground mb-4">Save time by creating reusable task templates for common workflows.</p>
          <Button onClick={onShowUpgrade} className="bg-gradient-to-r from-primary to-purple-600"><Crown className="h-4 w-4 mr-2" />Upgrade to Pro</Button>
        </div>
      </div>
    );
  }

  const handleCreate = () => {
    if (!newTemplate.name.trim() || !newTemplate.title.trim()) { toast.error('Name and title are required'); return; }
    onCreateTemplate(newTemplate);
    setNewTemplate({ name: '', title: '', description: '', priority: 'medium', subtasks: [], tag_ids: [] });
    setShowCreateModal(false);
  };

  const addSubtask = () => {
    if (newSubtask.trim()) {
      setNewTemplate({ ...newTemplate, subtasks: [...newTemplate.subtasks, { title: newSubtask.trim(), completed: false }] });
      setNewSubtask('');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2"><FileText className="h-5 w-5 text-primary" />Task Templates</h2>
        <Button onClick={() => setShowCreateModal(true)}><Plus className="h-4 w-4 mr-2" />New Template</Button>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border-2 border-dashed">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No templates yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Create your first template to speed up task creation</p>
          <Button variant="outline" onClick={() => setShowCreateModal(true)}><Plus className="h-4 w-4 mr-2" />Create Template</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(template => (
            <div key={template.id} className="bg-card rounded-xl border p-4 hover:shadow-md transition-shadow" data-testid={`template-${template.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div><h4 className="font-semibold">{template.name}</h4><p className="text-sm text-muted-foreground">{template.title}</p></div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end"><DropdownMenuItem onClick={() => onDeleteTemplate(template.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem></DropdownMenuContent>
                </DropdownMenu>
              </div>
              {template.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{template.description}</p>}
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className={`text-xs ${PRIORITY_CONFIG[template.priority || 'medium'].className}`}>{PRIORITY_CONFIG[template.priority || 'medium'].label}</Badge>
                {template.subtasks?.length > 0 && <Badge variant="outline" className="text-xs"><CheckSquare className="h-3 w-3 mr-1" />{template.subtasks.length} subtasks</Badge>}
              </div>
              <Select onValueChange={(columnId) => onUseTemplate(template.id, columnId)}>
                <SelectTrigger className="w-full h-9"><SelectValue placeholder="Use template in..." /></SelectTrigger>
                <SelectContent>{columns.map(col => (<SelectItem key={col.id} value={col.id}><div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ backgroundColor: col.color }} />{col.name}</div></SelectItem>))}</SelectContent>
              </Select>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-primary" />Create Template</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1.5 block">Template Name</label><Input placeholder="e.g., Weekly Report" value={newTemplate.name} onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Task Title</label><Input placeholder="e.g., Write weekly report" value={newTemplate.title} onChange={(e) => setNewTemplate({ ...newTemplate, title: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Description</label><Textarea placeholder="Task description..." value={newTemplate.description} onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })} rows={2} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Priority</label>
              <Select value={newTemplate.priority} onValueChange={(v) => setNewTemplate({ ...newTemplate, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-sm font-medium mb-1.5 block">Subtasks</label>
              <div className="space-y-2">
                {newTemplate.subtasks.map((st, i) => (
                  <div key={i} className="flex items-center gap-2"><Square className="h-4 w-4 text-muted-foreground" /><span className="flex-1 text-sm">{st.title}</span><Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setNewTemplate({ ...newTemplate, subtasks: newTemplate.subtasks.filter((_, idx) => idx !== i) })}><X className="h-3 w-3" /></Button></div>
                ))}
                <div className="flex gap-2"><Input placeholder="Add subtask..." value={newSubtask} onChange={(e) => setNewSubtask(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSubtask())} className="h-8 text-sm" /><Button type="button" size="sm" variant="outline" onClick={addSubtask} className="h-8">Add</Button></div>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button><Button onClick={handleCreate}>Create Template</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const ExportPanel = ({ isPro, onShowUpgrade }) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'opero_export.csv'); document.body.appendChild(link); link.click(); link.remove();
      toast.success('CSV exported successfully!');
    } catch (e) { if (e.response?.status === 403) onShowUpgrade(); else toast.error('Export failed'); } finally { setIsExporting(false); }
  };

  const handleExportJSON = async () => {
    setIsExporting(true);
    try {
      const response = await axios.get(`${API}/export/json`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'opero_export.json'); document.body.appendChild(link); link.click(); link.remove();
      toast.success('JSON exported successfully!');
    } catch (e) { if (e.response?.status === 403) onShowUpgrade(); else toast.error('Export failed'); } finally { setIsExporting(false); }
  };

  if (!isPro) {
    return (
      <div className="p-8 text-center">
        <div className="max-w-md mx-auto">
          <Download className="h-16 w-16 text-primary mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold mb-2">Unlock Export Reports</h3>
          <p className="text-muted-foreground mb-4">Export your tasks and data in CSV or JSON format for backup or analysis.</p>
          <Button onClick={onShowUpgrade} className="bg-gradient-to-r from-primary to-purple-600"><Crown className="h-4 w-4 mr-2" />Upgrade to Pro</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6 flex items-center gap-2"><Download className="h-5 w-5 text-primary" />Export Reports</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <button onClick={handleExportCSV} disabled={isExporting} className="p-6 bg-card rounded-xl border hover:border-primary hover:shadow-md transition-all text-left" data-testid="export-csv-btn">
          <div className="flex items-center gap-3 mb-3"><div className="p-3 bg-emerald-500/10 rounded-lg"><FileText className="h-6 w-6 text-emerald-500" /></div><div><h3 className="font-semibold">CSV Export</h3><p className="text-sm text-muted-foreground">Spreadsheet format</p></div></div>
          <p className="text-xs text-muted-foreground">Export all tasks as a CSV file. Compatible with Excel, Google Sheets, and other spreadsheet apps.</p>
        </button>
        <button onClick={handleExportJSON} disabled={isExporting} className="p-6 bg-card rounded-xl border hover:border-primary hover:shadow-md transition-all text-left" data-testid="export-json-btn">
          <div className="flex items-center gap-3 mb-3"><div className="p-3 bg-blue-500/10 rounded-lg"><FileText className="h-6 w-6 text-blue-500" /></div><div><h3 className="font-semibold">JSON Export</h3><p className="text-sm text-muted-foreground">Full data backup</p></div></div>
          <p className="text-xs text-muted-foreground">Export all data including tasks, columns, categories, and tags as JSON for complete backup.</p>
        </button>
      </div>
    </div>
  );
};

export const ProjectsView = ({ projects, onCreateProject, onUpdateProject, onDeleteProject, onSelectProject, selectedProject, teams, members = [], onShareProject, onRevokeShare }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', color: '#6366F1', icon: 'folder', team_id: '', lead_user_id: '' });
  const PROJECT_COLORS = ['#6366F1', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#14B8A6'];
  const userMembers = members.filter(m => m.type === 'user');
  const leadName = (id) => userMembers.find(m => m.id === id)?.name;

  const handleCreate = async () => {
    if (!newProject.name.trim()) { toast.error('Project name is required'); return; }
    const data = { ...newProject };
    if (!data.team_id) delete data.team_id;
    if (!data.lead_user_id) delete data.lead_user_id;
    await onCreateProject(data);
    setNewProject({ name: '', description: '', color: '#6366F1', icon: 'folder', team_id: '', lead_user_id: '' });
    setShowCreateModal(false);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center gap-2"><FolderKanban className="h-5 w-5 text-primary" />Projects</h2>
        <Button onClick={() => setShowCreateModal(true)}><FolderPlus className="h-4 w-4 mr-2" />New Project</Button>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border-2 border-dashed">
          <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No projects yet</h3>
          <p className="text-sm text-muted-foreground mb-4">Create your first project to organize your tasks</p>
          <Button variant="outline" onClick={() => setShowCreateModal(true)}><FolderPlus className="h-4 w-4 mr-2" />Create Project</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.map(project => (
            <div key={project.id} onClick={() => onSelectProject(project.id)} className={`bg-card rounded-xl border p-4 hover:shadow-md transition-all text-left cursor-pointer ${selectedProject === project.id ? 'border-primary ring-2 ring-primary/20' : ''}`} data-testid={`project-${project.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${project.color}15` }}><FolderKanban className="h-5 w-5" style={{ color: project.color }} /></div>
                  <div><h4 className="font-semibold">{project.name}</h4>{project.description && <p className="text-xs text-muted-foreground line-clamp-1">{project.description}</p>}</div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onShareProject && <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onShareProject(project.id); }} data-testid={`share-project-${project.id}`}><Share2 className="h-4 w-4 mr-2" />Share Link</DropdownMenuItem>}
                    {onUpdateProject && userMembers.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Project lead</div>
                        {userMembers.map(m => (
                          <DropdownMenuItem key={m.id} onClick={(e) => { e.stopPropagation(); onUpdateProject(project.id, { lead_user_id: project.lead_user_id === m.id ? '' : m.id }); }} className={project.lead_user_id === m.id ? 'bg-primary/10 text-primary font-medium' : ''} data-testid={`set-lead-${project.id}-${m.id}`}>
                            <UserCheck className="h-4 w-4 mr-2" />{m.name}{project.lead_user_id === m.id ? ' ✓' : ''}
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDeleteProject(project.id); }} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5 text-muted-foreground"><ListTodo className="h-4 w-4" /><span>{project.task_count || 0} tasks</span></div>
                {project.lead_user_id && leadName(project.lead_user_id) && (
                  <div className="flex items-center gap-1.5" data-testid={`project-lead-${project.id}`}>
                    <span className="w-5 h-5 rounded-full bg-violet-500/15 text-violet-600 text-[9px] font-bold flex items-center justify-center">{leadName(project.lead_user_id).split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}</span>
                    <span className="text-xs text-muted-foreground">{leadName(project.lead_user_id)}</span>
                  </div>
                )}
                {project.team_name && <div className="flex items-center gap-1.5 text-muted-foreground"><span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{project.team_name}</span></div>}
                {project.task_count > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.round((project.completed_count / project.task_count) * 100)}%` }} /></div>
                    <span className="text-xs text-muted-foreground">{Math.round((project.completed_count / project.task_count) * 100)}%</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderPlus className="h-5 w-5 text-primary" />Create Project</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1.5 block">Project Name</label><Input placeholder="e.g., Website Redesign" value={newProject.name} onChange={(e) => setNewProject({ ...newProject, name: e.target.value })} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Description</label><Textarea placeholder="Brief description..." value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} rows={2} /></div>
            <div><label className="text-sm font-medium mb-1.5 block">Color</label>
              <div className="flex gap-2">{PROJECT_COLORS.map(color => (<button key={color} onClick={() => setNewProject({ ...newProject, color })} className={`w-8 h-8 rounded-full transition-transform ${newProject.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`} style={{ backgroundColor: color }} />))}</div>
            </div>
            {teams?.length > 0 && (
              <div><label className="text-sm font-medium mb-1.5 block">Team (optional)</label>
                <Select value={newProject.team_id || "none"} onValueChange={(v) => setNewProject({ ...newProject, team_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="No team" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No team (personal)</SelectItem>
                    {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {userMembers.length > 0 && (
              <div><label className="text-sm font-medium mb-1.5 block">Project lead (optional)</label>
                <Select value={newProject.lead_user_id || "none"} onValueChange={(v) => setNewProject({ ...newProject, lead_user_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="project-lead-select"><SelectValue placeholder="No lead" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No lead</SelectItem>
                    {userMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button><Button onClick={handleCreate}>Create Project</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const GanttView = ({ tasks, onSelectTask }) => {
  const [viewMode, setViewMode] = useState(ViewMode.Week);
  const [ganttTasks, setGanttTasks] = useState([]);

  useEffect(() => {
    const transformed = tasks
      .filter(t => t.due_date || t.created_at)
      .map(task => {
        let start, end;
        if (task.due_date) {
          end = new Date(task.due_date);
          if (task.created_at) { start = new Date(task.created_at); } else { start = new Date(end); start.setDate(start.getDate() - 3); }
        } else if (task.created_at) {
          start = new Date(task.created_at);
          end = new Date(start); end.setDate(end.getDate() + 7);
        } else { return null; }
        return { start, end, name: task.title, id: task.id, type: 'task', progress: task.completed ? 100 : 0, dependencies: task.blocked_by || [], styles: { progressColor: task.completed ? '#10B981' : '#6366F1', progressSelectedColor: '#4F46E5' } };
      }).filter(Boolean);
    setGanttTasks(transformed);
  }, [tasks]);

  if (ganttTasks.length === 0) {
    return (
      <div className="p-6 text-center">
        <GanttChartIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-semibold mb-2">No tasks with dates</h3>
        <p className="text-sm text-muted-foreground">Add due dates to your tasks to see them in the Gantt chart</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2"><GanttChartIcon className="h-5 w-5 text-primary" />Timeline</h2>
        <div className="flex gap-2">
          <Button variant={viewMode === ViewMode.Day ? "default" : "outline"} size="sm" onClick={() => setViewMode(ViewMode.Day)}>Day</Button>
          <Button variant={viewMode === ViewMode.Week ? "default" : "outline"} size="sm" onClick={() => setViewMode(ViewMode.Week)}>Week</Button>
          <Button variant={viewMode === ViewMode.Month ? "default" : "outline"} size="sm" onClick={() => setViewMode(ViewMode.Month)}>Month</Button>
        </div>
      </div>
      <div className="bg-card rounded-xl border overflow-hidden">
        <Gantt tasks={ganttTasks} viewMode={viewMode} onDoubleClick={(task) => onSelectTask(tasks.find(t => t.id === task.id))} listCellWidth="" columnWidth={viewMode === ViewMode.Month ? 150 : viewMode === ViewMode.Week ? 60 : 30} barCornerRadius={4} barFill={60} handleWidth={8} todayColor="rgba(99, 102, 241, 0.1)" />
      </div>
    </div>
  );
};

export const ActivityView = ({ activities }) => {
  const getActivityIcon = (action) => {
    switch(action) {
      case 'created': return <Plus className="h-4 w-4 text-emerald-500" />;
      case 'updated': return <Edit className="h-4 w-4 text-blue-500" />;
      case 'completed': return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      case 'comment_added': return <MessageCircle className="h-4 w-4 text-purple-500" />;
      case 'attachment_added': return <Paperclip className="h-4 w-4 text-amber-500" />;
      case 'dependency_added': return <Link2 className="h-4 w-4 text-blue-500" />;
      default: return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActivityText = (activity) => {
    switch(activity.action) {
      case 'created': return `Created "${activity.entity_title}"`;
      case 'updated': return `Updated "${activity.entity_title}"`;
      case 'completed': return `Completed "${activity.entity_title}"`;
      case 'comment_added': return `Added comment on "${activity.entity_title}"`;
      case 'attachment_added': return `Added attachment to "${activity.entity_title}"`;
      case 'dependency_added': return `Added dependency to "${activity.entity_title}"`;
      default: return `${activity.action} "${activity.entity_title}"`;
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-6 flex items-center gap-2"><History className="h-5 w-5 text-primary" />Activity Log</h2>
      {activities.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl border-2 border-dashed">
          <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No activity yet</h3>
          <p className="text-sm text-muted-foreground">Activity will appear here as you work on tasks</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.map(activity => (
            <div key={activity.id} className="flex items-start gap-3 p-3 bg-card rounded-lg border">
              <div className="p-2 bg-muted rounded-lg">{getActivityIcon(activity.action)}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{getActivityText(activity)}</p>
                <p className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
